import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Header from '../Header/Header';
import ProfileSidebar from './ProfileSidebar';
import ProfileInfo from './ProfileInfo';
import AddressManagement from './AddressManagement';
import Loader from './Loader';
import ErrorMessage from './ErrorMessage';
import AddAddressModal from './AddAddressModal';
import EditAddressModal from './EditAddressModal';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import Orders from './Orders';
const Profile = () => {
  const [userData, setUserData] = useState(null);
  const [shippingAddresses, setShippingAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('profile');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = Cookies.get("ecom_user_token");
        
        const [userResponse, addressesResponse] = await Promise.all([
          axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/buyer/get-by-id`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/buyer/get-shipping-addresses`, {
            headers: { Authorization: `Bearer ${token}` }
          })
        ]);
        
        setUserData(userResponse?.data?.user);
        setShippingAddresses(addressesResponse?.data?.shippingAddresses);
      } catch (err) {
        setError(err.response?.data?.message || "Failed to fetch data");
        toast.error("Failed to load profile data");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleAddAddress = async (newAddress) => {
    setIsProcessing(true);
    try {
      const token = Cookies.get("ecom_user_token");
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/buyer/add-shipping-address`,
        newAddress,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const addressesResponse = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/buyer/get-shipping-addresses`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setShippingAddresses(addressesResponse.data.shippingAddresses);
      setShowAddModal(false);
      toast.success("Address added successfully!");
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to add address");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleUpdateAddress = async (updatedAddress) => {
    setIsProcessing(true);
    try {
      const token = Cookies.get("ecom_user_token");
      await axios.patch(
        `${process.env.REACT_APP_BACKEND_URL}/api/buyer/update-shipping-address`,
        {
          addressId: selectedAddress._id,
          ...updatedAddress
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const addressesResponse = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/buyer/get-shipping-addresses`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setShippingAddresses(addressesResponse.data.shippingAddresses);
      setShowEditModal(false);
      toast.success("Address updated successfully!");
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update address");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDeleteAddress = async () => {
    setIsProcessing(true);
    try {
      const token = Cookies.get("ecom_user_token");
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/buyer/remove-shipping-address/${selectedAddress._id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const addressesResponse = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/buyer/get-shipping-addresses`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setShippingAddresses(addressesResponse.data.shippingAddresses);
      setShowDeleteModal(false);
      toast.success("Address deleted successfully!");
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to delete address");
    } finally {
      setIsProcessing(false);
    }
  };

  const openEditModal = (address) => {
    setSelectedAddress(address);
    setShowEditModal(true);
  };

  const openDeleteModal = (address) => {
    setSelectedAddress(address);
    setShowDeleteModal(true);
  };

  if (loading) return <Loader fullScreen />;
  if (error) return <ErrorMessage message={error} />;
  if (!userData) return <ErrorMessage message="No user data available" type="warning" />;

  return (
    <>
      <Header />
      
      <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-3xl font-bold text-gray-900 mb-8">My Account</h1>
          
          <div className="flex flex-col md:flex-row gap-8">
            <ProfileSidebar 
              userData={userData} 
              activeTab={activeTab} 
              setActiveTab={setActiveTab} 
            />
            
            <div className="w-full md:w-2/3 lg:w-3/4">
              {activeTab === 'profile' && <ProfileInfo userData={userData} />}
              
              {activeTab === 'addresses' && (
                <AddressManagement 
                  addresses={shippingAddresses}
                  onAddClick={() => setShowAddModal(true)}
                  onEditClick={openEditModal}
                  onDeleteClick={openDeleteModal}
                /> 
              )}
              {activeTab === 'orders' && <Orders />}
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      <AddAddressModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSubmit={handleAddAddress}
        isProcessing={isProcessing}
      />
      
      {selectedAddress && (
        <EditAddressModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          onSubmit={handleUpdateAddress}
          initialData={selectedAddress}
          isProcessing={isProcessing}
        />
      )}
      
      {selectedAddress && (
        <DeleteConfirmationModal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={handleDeleteAddress}
          isProcessing={isProcessing}
        />
      )}
      
    </>
  );
};

export default Profile;